import type {
  ResetPassword,
  SecondStep,
  SecondStepNeeded,
  Session,
  SignIn,
  SignUp,
} from '@cyberscore/contracts';
import { ApiError, type FieldIssue } from './api';

/**
 * The API's own sign-in (decision 0033), before anyone is signed in. The refresh token travels only in an
 * HttpOnly cookie the browser keeps; these calls carry the header that proves they come from this app.
 */
async function call<T>(path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api/auth/${path}`, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json', 'x-cyberscore-csrf': '1' },
    body: JSON.stringify(body ?? {}),
    credentials: 'same-origin',
  }).catch(() => {
    throw new ApiError(0, 'Could not reach the server. Check your connection.', 'network', []);
  });
  const data = res.status === 204 ? null : await res.json().catch(() => null);
  if (res.ok) return data as T;
  const d = (data ?? {}) as { message?: unknown; error?: string; issues?: FieldIssue[] };
  const message =
    (typeof d.message === 'string' && d.message) ||
    (Array.isArray(d.message) && d.message.join('; ')) ||
    `Request failed (${res.status})`;
  throw new ApiError(res.status, message, d.error ?? null, Array.isArray(d.issues) ? d.issues : []);
}

export const accounts = {
  signUp: (b: SignUp) => call<{ message: string }>('sign-up', b),
  signIn: (b: SignIn) => call<Session | SecondStepNeeded>('sign-in', b),
  secondStep: (b: SecondStep) => call<Session>('sign-in/second-step', b),
  verify: (token: string) => call<Session>('verify-email', { token }),
  resend: (email: string) => call<{ message: string }>('resend-verification', { email }),
  forgot: (email: string) => call<{ message: string }>('password/forgot', { email }),
  reset: (b: ResetPassword) => call<Session>('password/reset', b),
  /** Null when there is no session to restore. */
  refresh: () => call<Session | null>('refresh'),
  signOut: () => call<null>('sign-out'),
};

/** The field a server error points at, for showing it next to that field. */
export const issueFor = (e: unknown, path: string) =>
  e instanceof ApiError ? (e.issues.find((i) => i.path === path)?.message ?? null) : null;
